import { useState, useEffect } from 'react'
import { useToast } from '../context/ToastContext'
import { supabase } from '../lib/supabase'

export default function Auth({ onAuthSuccess }: { onAuthSuccess: (token: string) => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'login' | 'register' | 'forgot-password' | 'update-password' | 'verify-signup' | 'verify-recovery'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    // Listen for the password recovery event
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setMode('update-password');
      } else if (event === 'SIGNED_IN' && session && mode !== 'update-password' && mode !== 'verify-recovery') {
        // Only auto-redirect if we aren't trying to update the password or verify a recovery code
        onAuthSuccess(session.access_token);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [mode, onAuthSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === 'register') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
            }
          }
        });
        if (error) throw error;
        if (data.session) {
          onAuthSuccess(data.session.access_token);
        } else {
          showToast('Please check your email for the 8-digit verification code.', 'success');
          setMode('verify-signup');
        }
      } else if (mode === 'verify-signup' || mode === 'verify-recovery') {
        const { data, error } = await supabase.auth.verifyOtp({
          email,
          token: otp,
          type: mode === 'verify-signup' ? 'signup' : 'recovery'
        });
        if (error) throw error;
        
        if (mode === 'verify-signup') {
          showToast('Email verified successfully!', 'success');
          if (data.session) onAuthSuccess(data.session.access_token);
        } else {
          showToast('Code verified! Please set your new password.', 'success');
          setMode('update-password');
        }
      } else if (mode === 'forgot-password') {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        showToast('Password reset code has been sent to your email!', 'success');
        setMode('verify-recovery');
      } else if (mode === 'update-password') {
        const { error } = await supabase.auth.updateUser({
          password: password
        });
        if (error) throw error;
        showToast('Password updated successfully!', 'success');
        
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          onAuthSuccess(sessionData.session.access_token);
        } else {
          setMode('login');
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        if (data.session) {
          onAuthSuccess(data.session.access_token);
        }
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
    } catch (err: any) {
      console.error(err);
      showToast(err.message, 'error');
    }
  };

  return (
    <div className="w-full max-w-[420px] mx-auto">
      <div className="bg-[var(--background2)] border border-[var(--border1)] rounded-xl shadow-2xl p-8 flex flex-col items-center justify-center relative overflow-hidden">
        
        {/* Subtle background accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[var(--blueBorder)] to-[#4353FF] opacity-80"></div>
        
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 overflow-hidden bg-white/5 border border-white/10 shadow-sm">
          <img src="/logo.png" alt="CMS Auditor Logo" className="w-full h-full object-cover" />
        </div>

        <div className="text-center mb-8 w-full">
          <h2 className="text-2xl font-bold text-[var(--text1)] mb-3 tracking-tight">
            {mode === 'login' ? 'Welcome back' : mode === 'register' ? 'Create an account' : mode === 'update-password' ? 'Set new password' : mode === 'forgot-password' ? 'Reset password' : 'Enter verification code'}
          </h2>
          <p className="text-[14px] md:text-[15px] text-[var(--text2)] leading-relaxed">
            {mode === 'forgot-password' ? 'Enter your email address to receive password reset instructions.' : mode === 'update-password' ? 'Enter your new password below.' : mode === 'verify-signup' || mode === 'verify-recovery' ? 'Please check your email for the 8-digit code we sent you.' : 'CMS Auditor helps you maintain the quality and consistency of your CMS content.'}
          </p>
        </div>

        {mode !== 'update-password' && mode !== 'verify-signup' && mode !== 'verify-recovery' && (
          <div className="w-full flex bg-[var(--background3)] rounded-md p-1 mb-6">
            <button 
              type="button"
              className={`flex-1 py-1.5 text-[13px] md:text-[14px] font-medium rounded-sm transition-colors ${mode === 'login' || mode === 'forgot-password' ? 'bg-[var(--background1)] text-[var(--text1)] shadow-sm' : 'text-[var(--text3)] hover:text-[var(--text2)]'}`}
              onClick={() => setMode('login')}
            >
              Login
            </button>
            <button 
              type="button"
              className={`flex-1 py-1.5 text-[13px] md:text-[14px] font-medium rounded-sm transition-colors ${mode === 'register' ? 'bg-[var(--background1)] text-[var(--text1)] shadow-sm' : 'text-[var(--text3)] hover:text-[var(--text2)]'}`}
              onClick={() => setMode('register')}
            >
              Register
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
          {mode === 'verify-signup' || mode === 'verify-recovery' ? (
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] md:text-[13px] font-medium text-[var(--text2)]">8-Digit Verification Code</label>
              <input 
                type="text" 
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="12345678"
                required
                disabled={isLoading}
                maxLength={8}
                className="bg-[var(--background1)] border border-[var(--border2)] text-[var(--text1)] rounded-[4px] px-3 py-2 text-[18px] md:text-[19px] focus:outline-none focus:border-[var(--blueBorder)] tracking-[0.5em] text-center font-mono"
              />
            </div>
          ) : (
            <>
          {mode === 'register' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] md:text-[13px] font-medium text-[var(--text2)]">Name</label>
              <input 
                type="text" 
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="John Doe"
                required
                disabled={isLoading}
                className="bg-[var(--background1)] border border-[var(--border2)] text-[var(--text1)] rounded-[4px] px-3 py-2 text-[13px] md:text-[14px] focus:outline-none focus:border-[var(--blueBorder)]"
              />
            </div>
          )}
          {mode !== 'update-password' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] md:text-[13px] font-medium text-[var(--text2)]">Email</label>
              <input 
                type="email" 
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={isLoading}
                className="bg-[var(--background1)] border border-[var(--border2)] text-[var(--text1)] rounded-[4px] px-3 py-2 text-[13px] md:text-[14px] focus:outline-none focus:border-[var(--blueBorder)]"
              />
            </div>
          )}
          {mode !== 'forgot-password' && (
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between items-center">
                <label className="text-[12px] md:text-[13px] font-medium text-[var(--text2)]">{mode === 'update-password' ? 'New Password' : 'Password'}</label>
                {mode === 'login' && (
                  <button type="button" onClick={() => setMode('forgot-password')} className="text-[12px] md:text-[13px] text-[var(--blueBorder)] hover:underline">
                    Forgot Password?
                  </button>
                )}
              </div>
              <input 
                type="password" 
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoading}
                className="bg-[var(--background1)] border border-[var(--border2)] text-[var(--text1)] rounded-[4px] px-3 py-2 text-[13px] md:text-[14px] focus:outline-none focus:border-[var(--blueBorder)]"
              />
            </div>
          )}
          </>)}

          <button 
            type="submit"
            disabled={isLoading}
            className="w-full relative group overflow-hidden mt-2 bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] font-medium py-2.5 px-4 rounded-[4px] transition-all duration-200 shadow-sm flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <span>{mode === 'login' ? 'Login' : mode === 'register' ? 'Create Account' : mode === 'update-password' ? 'Update Password' : mode === 'verify-signup' || mode === 'verify-recovery' ? 'Verify Code' : 'Send Reset Code'}</span>
            )}
          </button>
        </form>

        {mode !== 'update-password' && mode !== 'verify-signup' && mode !== 'verify-recovery' && (
          <>
            <div className="w-full mt-6 mb-2 flex items-center justify-center gap-3">
              <div className="h-px bg-[var(--border2)] flex-1"></div>
              <span className="text-[12px] md:text-[13px] text-[var(--text3)] font-medium">OR</span>
              <div className="h-px bg-[var(--border2)] flex-1"></div>
            </div>

            <button 
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full mt-2 bg-[var(--background1)] hover:bg-[var(--background3)] border border-[var(--border2)] text-[var(--text1)] font-medium py-2.5 px-4 rounded-[4px] transition-colors duration-200 flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </>
        )}
      </div>
    </div>
  )
}
